/**
 * 企业微信 OAuth 网页授权工具
 *
 * 支持两种授权模式，可通过环境变量 VUE_APP_WX_AUTH_MODE 或 URL 参数 authMode 切换：
 *  - frontend（默认）：前端跳转企业微信授权页 -> 回跳携带 code -> 前端把 code 传给后端换取 token
 *  - backend：跳转后端下发的授权地址（userInfo.unauthorizedUrl）完成企业微信登录 -> 回跳携带 accessToken
 */

// 企业 ID（CorpID），通过 .env 中的 VUE_APP_WX_APPID 配置（仅 frontend 模式使用）
const WX_APPID: string = process.env.VUE_APP_WX_APPID || "";

// 自建应用 AgentId，通过 .env 中的 VUE_APP_WX_AGENTID 配置（仅 frontend 模式使用）
const WX_AGENTID: string = process.env.VUE_APP_WX_AGENTID || "";

// frontend 模式下，code 换取 token 的接口：GET /wework/login?code=xxx
// （axios 401 拦截时据此精确匹配排除授权接口自身，避免无限循环）
export const WX_AUTH_API_PATH = "/wework/login";
// export const WX_AUTH_API_PATH = "/wework/login/login1";

// 授权作用域：snsapi_base 静默授权（无需用户确认，后端凭 code 换取成员身份）
const WX_SCOPE = "snsapi_base";

// 随机 state 存储 key，用于防止 CSRF
const STATE_KEY = "wx_oauth_state";

// 授权跳转前记录的业务回跳路由（含 hash 内 query），仅重新授权成功后恢复
const AUTH_RETURN_ROUTE_KEY = "wx_auth_return_route";
const AUTH_REAUTH_KEY = "wx_auth_reauth";
const AUTH_HISTORY_KEY = "wx_auth_history";
const AUTH_RETURN_ROUTE_MAX_AGE = 10 * 60 * 1000;

export const markReauthorization = (): void => {
  sessionStorage.setItem(AUTH_REAUTH_KEY, "1");
};

export const consumeReauthorization = (): boolean => {
  const marked = sessionStorage.getItem(AUTH_REAUTH_KEY) === "1";
  sessionStorage.removeItem(AUTH_REAUTH_KEY);
  return marked;
};

interface AuthReturnRoutePayload {
  route: string;
  savedAt: number;
}

/**
 * 记录授权前的业务路由（含 query）。
 * 当前项目使用 History 路由，同时兼容历史分享出去的 Hash 路由地址。
 */
export const rememberAuthReturnRoute = (): void => {
  const hash = window.location.hash;
  let route = hash.startsWith("#/") ? hash.slice(1) : "";

  if (!route) {
    const base = (process.env.BASE_URL || "/").replace(/\/$/, "");
    const pathname = window.location.pathname;
    const routePath =
      base && pathname.startsWith(`${base}/`)
        ? pathname.slice(base.length)
        : pathname === base
        ? "/"
        : pathname;
    const params = new URLSearchParams(window.location.search);
    ["code", "state", "accessToken", "authMode"].forEach((key) =>
      params.delete(key)
    );
    const search = params.toString();
    route = `${routePath || "/"}${search ? `?${search}` : ""}`;
  }

  if (route && route !== "/") {
    const payload: AuthReturnRoutePayload = {
      route,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(AUTH_RETURN_ROUTE_KEY, JSON.stringify(payload));
  }
};

/**
 * 取出并清除已记录的回跳路由（一次性消费，避免后续导航误触发回跳）
 * @param maxAge 最大保留时长，超过则视为过期并丢弃
 */
export const takeAuthReturnRoute = (
  maxAge = AUTH_RETURN_ROUTE_MAX_AGE
): string | null => {
  const raw = sessionStorage.getItem(AUTH_RETURN_ROUTE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(AUTH_RETURN_ROUTE_KEY);
  try {
    const payload = JSON.parse(raw) as Partial<AuthReturnRoutePayload>;
    if (
      typeof payload.route === "string" &&
      payload.route &&
      typeof payload.savedAt === "number" &&
      Date.now() - payload.savedAt <= maxAge
    ) {
      return payload.route;
    }
  } catch {
    return null;
  }
  return null;
};

// 授权模式 sessionStorage key，用于 URL 参数临时切换（免重启）
const AUTH_MODE_KEY = "wx_auth_mode";

export type WxAuthMode = "frontend" | "backend";

// 全局开关：401/未登录时是否自动跳转授权页
// true  - 跳转（默认行为）
// false - 不跳转，由前端自行处理（如弹窗提示）
export const ENABLE_401_REDIRECT = true;

/**
 * 获取当前授权模式
 * 优先级：URL 参数/sessionStorage 临时切换 > 环境变量 VUE_APP_WX_AUTH_MODE > 默认 frontend
 */
export const getAuthMode = (): WxAuthMode => {
  const override = sessionStorage.getItem(AUTH_MODE_KEY);
  if (override === "frontend" || override === "backend") {
    return override;
  }
  return (process.env.VUE_APP_WX_AUTH_MODE || "frontend") === "backend"
    ? "backend"
    : "frontend";
};

/**
 * 设置授权模式（存 sessionStorage，仅当前会话生效）
 * 传 null 可清除临时切换，恢复环境变量配置
 */
export const setAuthMode = (mode: WxAuthMode | null): void => {
  if (mode === null) {
    sessionStorage.removeItem(AUTH_MODE_KEY);
  } else {
    sessionStorage.setItem(AUTH_MODE_KEY, mode);
  }
};

/**
 * 生成随机 state 并存入 sessionStorage，用于微信回调时校验，防止 CSRF
 */
const createState = (): string => {
  const state = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(STATE_KEY, state);
  return state;
};

/**
 * 校验微信回调携带的 state
 * 参数来自 vue-router 的 query，类型为 LocationQueryValue | LocationQueryValue[]
 */
export const checkState = (state: unknown): boolean => {
  const saved = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  return !!saved && saved === state;
};

// 「刚完成授权」标记 key：回跳换取到新 token 时写入
const AUTH_FRESH_KEY = "wx_auth_fresh";

/**
 * 标记本轮导航刚通过授权拿到全新会话
 * 用于区分「本次授权新建的会话」和「更早访问遗留的旧会话」：
 * 管理端解绑等后台操作只影响存量会话的一致性，新会话永远是干净的
 */
export const markAuthFresh = (): void => {
  sessionStorage.setItem(AUTH_FRESH_KEY, String(Date.now()));
};

/**
 * 读取并消费「刚完成授权」标记（读即清除，避免后续导航重复命中）
 */
export const consumeAuthFresh = (): boolean => {
  const marked = sessionStorage.getItem(AUTH_FRESH_KEY);
  if (!marked) return false;
  sessionStorage.removeItem(AUTH_FRESH_KEY);
  return true;
};

/**
 * 获取授权回跳地址
 * 直接回跳当前业务页，由当前页路由守卫解析 code；授权成功后再回退到授权前
 * 的同一条页面记录，避免把授权回跳页留在历史栈中。
 */
export const getRedirectUri = (): string => {
  const url = new URL(window.location.href);
  ["code", "state", "accessToken", "authMode"].forEach((key) =>
    url.searchParams.delete(key)
  );
  return url.toString();
};

/**
 * 拼装微信网页授权链接（frontend 模式）
 */
export const getWxAuthorizeUrl = (): string => {
  const redirectUri = encodeURIComponent(getRedirectUri());
  const state = createState();
  return (
    "https://open.weixin.qq.com/connect/oauth2/authorize" +
    `?appid=${WX_APPID}` +
    `&redirect_uri=${redirectUri}` +
    `&response_type=code` +
    `&scope=${WX_SCOPE}` +
    `&state=${state}` +
    // 企业自建应用需携带 agentid，标明发起授权的应用
    (WX_AGENTID ? `&agentid=${WX_AGENTID}` : "") +
    "#wechat_redirect"
  );
};

/**
 * 获取后端授权地址（backend 模式，本项目现有方案）
 * 后端在 getInfo 返回的用户信息中下发 unauthorizedUrl，随首页存入 localStorage.userInfo，
 * 未登录/登录失效时整页跳转该地址，授权完成后回跳携带 accessToken
 */
export const getBackendAuthUrl = (): string | undefined => {
  try {
    const userInfo = localStorage.getItem("userInfo");
    const unauthorizedUrl = userInfo
      ? JSON.parse(userInfo)?.unauthorizedUrl
      : undefined;
    return typeof unauthorizedUrl === "string" && unauthorizedUrl
      ? unauthorizedUrl
      : undefined;
  } catch (e) {
    console.error("读取 unauthorizedUrl 失败：", e);
    return undefined;
  }
};

/**
 * 获取当前模式对应的授权跳转地址
 * backend 模式可能返回 undefined（首次进入尚未登录，后端还没下发 unauthorizedUrl）
 */
export const getAuthUrl = (): string | undefined => {
  return getAuthMode() === "backend"
    ? getBackendAuthUrl()
    : getWxAuthorizeUrl();
};

/**
 * 跳转授权页
 * 带最小间隔保护，避免并发/连续触发导致无限跳转
 * @returns 是否真正发起了跳转（被节流拦截或无可用授权地址时返回 false）
 */
let lastRedirectTime = 0;
export const redirectToWxAuth = (): boolean => {
  const now = Date.now();
  const hadTokenBeforeRedirect = Boolean(getAuthToken());
  const hadReauthMarker = sessionStorage.getItem(AUTH_REAUTH_KEY) === "1";
  // 3 秒内不重复跳转，防止授权失败后陷入死循环
  if (now - lastRedirectTime < 3000 || isAuthRedirecting()) {
    return false;
  }
  const authUrl = getAuthUrl();
  if (!authUrl) {
    return false;
  }
  lastRedirectTime = now;
  sessionStorage.setItem(AUTH_REDIRECTING_KEY, String(now));
  // 仅在 token 已失效的重新授权场景记录原页面；首次授权统一进入首页
  if (hadTokenBeforeRedirect || hadReauthMarker) {
    markReauthorization();
    rememberAuthReturnRoute();
  } else {
    // 清掉上一次异常中断留下的状态，首次授权不得恢复旧页面
    sessionStorage.removeItem(AUTH_REAUTH_KEY);
    sessionStorage.removeItem(AUTH_RETURN_ROUTE_KEY);
  }
  // 使用 href 保留授权前页面；回调成功后由路由守卫 history.go(-1) 回到这条记录，
  // 直接移除授权回跳页，避免企业微信 WebView 中出现两个相同业务页面。
  if (getAuthMode() === "frontend") {
    sessionStorage.setItem(AUTH_HISTORY_KEY, "1");
  }
  window.location.href = authUrl;
  return true;
};

export const consumeAuthHistory = (): boolean => {
  const marked = sessionStorage.getItem(AUTH_HISTORY_KEY) === "1";
  sessionStorage.removeItem(AUTH_HISTORY_KEY);
  return marked;
};

// 授权失败时间戳 key，用于失败退避，防止后端异常时无限重定向
const AUTH_FAIL_KEY = "wx_auth_fail_time";

// 授权跳转锁：页面恢复可见检查和接口 401 可能同时触发授权，只允许发起一次
const AUTH_REDIRECTING_KEY = "wx_auth_redirecting";
const AUTH_REDIRECTING_TIMEOUT = 2 * 60 * 1000;

export const clearAuthRedirecting = (): void => {
  sessionStorage.removeItem(AUTH_REDIRECTING_KEY);
};

const isAuthRedirecting = (): boolean => {
  const startedAt = Number(sessionStorage.getItem(AUTH_REDIRECTING_KEY) || 0);
  if (!startedAt) return false;
  if (Date.now() - startedAt > AUTH_REDIRECTING_TIMEOUT) {
    clearAuthRedirecting();
    return false;
  }
  return true;
};

/**
 * 记录一次授权失败（如 code 换取 token 失败）
 */
export const recordAuthFail = (): void => {
  sessionStorage.setItem(AUTH_FAIL_KEY, String(Date.now()));
};

/**
 * 判断是否允许再次发起授权（失败退避：默认 30 秒内不重复发起）
 */
export const shouldRetryAuth = (interval = 30000): boolean => {
  const last = Number(sessionStorage.getItem(AUTH_FAIL_KEY) || 0);
  return Date.now() - last > interval;
};

// ===== token 有效期管理 =====

// 本地联调写死的 token（仅非生产构建生效，生产构建不含该值）。
// 启用后：模块加载时即写入该 token、跳过过期校验，且任何清除逻辑都不会将其移除，
// 方便本地不走企业微信授权直接调试页面。
// 临时换 token 可通过 .env.development 的 VUE_APP_DEBUG_TOKEN 覆盖，置空则用下面的默认值
const DEV_FALLBACK_TOKEN = "";

const MOCK_TOKEN: string =
  process.env.NODE_ENV !== "production"
    ? process.env.VUE_APP_DEBUG_TOKEN || DEV_FALLBACK_TOKEN
    : "";

if (MOCK_TOKEN) {
  localStorage.setItem("token", MOCK_TOKEN);
  // eslint-disable-next-line no-console
  console.log("[debug] 本地联调模式已启用，固定 token:", MOCK_TOKEN);
}

// token 过期时间戳存储 key（localStorage）
const TOKEN_EXPIRE_KEY = "wx_token_expire_at";

// 提前过期缓冲：在真正过期前 N 毫秒即视为过期，
// 避免请求刚好在过期瞬间发出、被服务端以 401 拒绝后再走一轮授权
const TOKEN_EXPIRE_BUFFER = 60 * 1000;

/**
 * 保存 token 并记录过期时间
 * @param token 访问凭证
 * @param expiresIn 有效期（秒）；未传入时不记录过期时间，视为长期有效
 * （如 backend 模式回跳的 accessToken，后端未下发有效期）
 */
export const saveToken = (token: string, expiresIn?: number): void => {
  // 本地联调模式：锁定为写死的 token，忽略授权流程写入的其他凭证
  if (MOCK_TOKEN) {
    localStorage.setItem("token", MOCK_TOKEN);
    return;
  }
  // 授权成功即重置失败退避：否则上一次失败留下的 30 秒冷却期，
  // 会让本次新 token 再过期时的重授权被 shouldRetryAuth 无故拦下
  sessionStorage.removeItem(AUTH_FAIL_KEY);
  localStorage.setItem("token", token);
  if (typeof expiresIn === "number" && expiresIn > 0) {
    localStorage.setItem(
      TOKEN_EXPIRE_KEY,
      String(Date.now() + expiresIn * 1000)
    );
  } else {
    // backend 回跳没有 expires_in，必须清除上一枚 token 遗留的过期时间，
    // 否则新 token 会在首次路由导航时立即被误判为过期，再次发起授权。
    localStorage.removeItem(TOKEN_EXPIRE_KEY);
  }
};

/**
 * 获取当前应使用的登录凭证
 * 本地联调模式（配置了 VUE_APP_DEBUG_TOKEN）下恒返回写死的 token，
 * 其余情况读取 localStorage
 */
export const getAuthToken = (): string => {
  if (MOCK_TOKEN) return MOCK_TOKEN;
  return localStorage.getItem("token") || "";
};

/**
 * 清除 token 及过期时间
 */
export const clearToken = (): void => {
  // 本地联调模式：不清除，保证刷新/接口 401 后写死的 token 仍在
  if (MOCK_TOKEN) return;
  localStorage.removeItem("token");
  localStorage.removeItem(TOKEN_EXPIRE_KEY);
};

/**
 * 判断当前 token 是否已过期（含缓冲窗口）
 * 未记录过期时间时视为未过期（兼容 backend 模式等无 expires_in 的场景）
 */
export const isTokenExpired = (): boolean => {
  if (MOCK_TOKEN) return false;
  const expireAt = Number(localStorage.getItem(TOKEN_EXPIRE_KEY) || 0);
  if (!expireAt) return false;
  return Date.now() >= expireAt - TOKEN_EXPIRE_BUFFER;
};

// 认证服务前缀（企业微信授权换 token、JS-SDK 签名等独立服务）
// 开发环境经 /server-api 代理转发，生产环境由网关按前缀路由；
// 定义在 wechat.ts 供 axios 的 server() 方法与授权排除逻辑共用，避免重复定义
export const SERVER_API_PATH = process.env.VUE_APP_SERVER_API;
const BASE_API_PATH = process.env.VUE_APP_BASE_API;

/**
 * 判断请求地址是否为换取凭证的授权接口自身（401 时排除，避免无限重定向）
 * 兼容带/不带 baseURL 前缀两种形态：
 *  - bare path：/wework/login（Axios 1.x 响应拦截器 config.url 为原始路径）
 *  - 带前缀：/server-api/wework/login 或 /dev-api/wework/login（取决于 Axios 版本或请求配置）
 */
const isAuthApiRequest = (url?: string): boolean => {
  if (!url) return false;
  if (url === WX_AUTH_API_PATH) return true;
  // 带前缀的完整路径比较
  return (
    (!!SERVER_API_PATH && url === `${SERVER_API_PATH}${WX_AUTH_API_PATH}`) ||
    (!!BASE_API_PATH && url === `${BASE_API_PATH}${WX_AUTH_API_PATH}`)
  );
};

/**
 * 统一处理登录失效：清除本地凭证并尝试整页跳转授权页重新获取。
 * 业务 code 401 与 HTTP 状态 401 都必须走这里，否则会出现
 * 「token 被清掉/或残留，但没有任何路径重新授权」的死局。
 * @param requestUrl 当前请求的接口地址；换取凭证接口自身的 401 不再跳转，避免死循环
 * @returns 是否真正发起了授权跳转（被排除/退避/无授权地址时返回 false）
 */
export const handleUnauthorized = (requestUrl?: string): boolean => {
  const hadTokenBeforeUnauthorized = Boolean(getAuthToken());
  if (isAuthApiRequest(requestUrl)) {
    clearToken();
    return false;
  }
  if (!ENABLE_401_REDIRECT || !shouldRetryAuth()) {
    clearToken();
    return false;
  }
  if (hadTokenBeforeUnauthorized) {
    markReauthorization();
    rememberAuthReturnRoute();
  }
  recordAuthFail();
  const redirected = redirectToWxAuth();
  clearToken();
  return redirected;
};

// ===== 平台提示页（微信等非企业微信环境打开分享链接）=====

/**
 * 跳转到平台提示页：后端在任意接口返回特定业务 code（见 axios 的
 * RequestEnums.PLATFORM）时，说明当前是从微信等非企业微信环境打开分享链接，
 * 前端不进入业务页面，整页跳转到 /platform/notice 提示用户在正确平台打开。
 *
 * 整页跳转（而非 router.push）：
 *  - 与 handleUnauthorized 的授权跳转保持一致，不依赖 router 实例，避免
 *    axios 与 router 互相 import 形成循环依赖；
 *  - 跳转目标为 public 路由，无需登录态，直接渲染提示页。
 */
export const redirectToPlatformNotice = (): void => {
  const base = (process.env.BASE_URL || "/").replace(/\/+$/, "");
  window.location.replace(`${base}/platform/notice`);
};
